import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Input, Row, Col } from 'reactstrap';
import BaseComponent from './BaseComponent';
import { QUOTE_FUNNEL } from 'helpers/sf_constants';
import { handleKeyDown } from 'helpers/quote_funnel_helper';

/**
 * InputComponent is a reusable React component that renders a form component field with an associated label and tooltip.
 * 
 * @param {Object} props - The props for the InputComponent.
 * @param {Object} props.label - An object containing properties related to the label.
 * @param {string} props.label.name - The name of the label.
 * @param {string} props.label.tooltip - The tooltip message for the label.
 * @param {Object} props.component - An object containing properties related to the component field.
 * @param {string} props.component.id - The ID of the component field.
 * @param {string} props.component.name - The name of the component field.
 * @param {string} props.component.type - The type of the component field (default is 'text').
 * @param {Object} props.validation - An object containing form validation properties.
 * @param {Function} props.validation.handleChange - The function to handle component changes.
 * @param {Function} props.validation.handleBlur - The function to handle component blur.
 * @param {Object} props.validation.values - An object containing form values.
 * @param {Object} props.validation.touched - An object indicating whether fields have been touched.
 * @param {Object} props.validation.errors - An object containing validation errors.
 * 
 * @returns {JSX.Element} - A React component that renders an component field with label and tooltip.
 */
const InputComponent = ({ index, question, onChangeEvent }) => {
  const [isFirstOrLastNameField, setIsFirstOrLastNameField] = useState(false)
  const SPECIAL_CASE_QUESTION_NAME_TYPES = {
    HEIGHT_FT: "height_ft",
    HEIGHT_IN: "height_in",
    PHONE_NUMBER: "phone_number",
    WEIGHT: 'weight'
  }
  useEffect(() => {
    setIsFirstOrLastNameField(question?.placeholder === "First Name" || question?.placeholder === "Last Name")
  }, []);

  const is_special_case = [
    SPECIAL_CASE_QUESTION_NAME_TYPES.HEIGHT_FT,
    SPECIAL_CASE_QUESTION_NAME_TYPES.HEIGHT_IN,
    SPECIAL_CASE_QUESTION_NAME_TYPES.WEIGHT,
    SPECIAL_CASE_QUESTION_NAME_TYPES.PHONE_NUMBER
  ]?.includes(question?.field_name);


  const isPhoneNumberType = question?.question_type === QUOTE_FUNNEL.COMPONENT_TYPE.PHONE_NUMBER;
  const IsHeightFt = question?.field_name === SPECIAL_CASE_QUESTION_NAME_TYPES.HEIGHT_FT;
  const isHeightIn = question?.field_name === SPECIAL_CASE_QUESTION_NAME_TYPES.HEIGHT_IN;
  const handleInputChange = (e) => {
    const value = e.target.value;
    if (question?.field_name === SPECIAL_CASE_QUESTION_NAME_TYPES.WEIGHT) {
      // Check if value is a number and has a length less than 4
      if (value?.length < 4 && value >= 0) {
        onChangeEvent(value);
      }
    }
    else if (question?.field_name === SPECIAL_CASE_QUESTION_NAME_TYPES.HEIGHT_FT) {
      // Check if value is a number and has a length less than 4
      if (value < 10 && value >= 0) {
        const form = e.target.form;
        const index = Array.prototype.indexOf.call(form, e.target);
        const nextInput = form.elements[index + 1];
        if (nextInput && value != '') {
          nextInput.focus();
        }
        onChangeEvent(value);
      }
    }
    else if (question?.field_name === SPECIAL_CASE_QUESTION_NAME_TYPES.HEIGHT_IN) {
      // Check if value is a number and has a length less than 4
      if (value < 12 && value >= 0) {
        onChangeEvent(value);
      }
    }
    else if (question?.placeholder === 'First Name' || question?.placeholder === 'Last Name') {
      const validInput = /^[A-Za-z]+(\s*[A-Za-z]*)*$/;

      if (validInput.test(value) || value === '') {
        onChangeEvent(value);
      }
    }
    else if (question?.placeholder === 'City') {
      const trimmedValue = value.replace(/\s+/g, ' ');
      if (trimmedValue.trim() !== '') {
        onChangeEvent(trimmedValue);
      }
      else {
        onChangeEvent('');
      }
    }
    else if (question?.placeholder === 'Business Name' || question?.field_name === "business_desc") {
      const validInput = /^[A-Za-z0-9!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]+(\s*[A-Za-z0-9\s!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]*)*$/;
      if (validInput.test(value) || value === '') {
        onChangeEvent(value);
      }
    }
    else {
      onChangeEvent(value);
    }
  };
  return (
    <BaseComponent question={is_special_case ? ({ ...question, question_label: null }) : ({ question })}>
      <Row key={index} className={`d-flex ${IsHeightFt ? 'justify-content-end' : isHeightIn ? 'justify-content-start' : 'justify-content-around'} align-items-center`}>
        <Col md={is_special_case ? 7 : isPhoneNumberType ? 6 : 12} className={`${is_special_case ? "col-sm-12 d-flex align-self-center" : ""} mb-2`}>
          <Input
            id={question?.id || ''}
            name={question?.field_name || ''}
            type={question?.question_type || 'text'}
            className={`quote-funnel-form-control ${isPhoneNumberType ? 'text-center' : ''} ${isFirstOrLastNameField ? 'text-capitalize' : ''}`}
            placeholder={`${isPhoneNumberType ? '(xxx) xxx-xxxx' : (question?.question_type === QUOTE_FUNNEL.COMPONENT_TYPE.TEXT && question?.original_field_name === "model") ? 'Enter vehicle model' : (question?.placeholder || '')}`}
            value={question?.value || ''}
            onChange={(e) => handleInputChange(e)}
            onKeyDown={handleKeyDown}
            autoFocus={index === 0}
          />

          {is_special_case && !isPhoneNumberType && (
            <div className='d-flex align-items-center ms-2'>
              <h1 className="quote-funnel-unique-h1 text-black">
                {question?.question_label}
              </h1>
            </div>
          )}
        </Col>
      </Row>
    </BaseComponent>
  );
}

InputComponent.propTypes = {
  label: PropTypes.object,
  component: PropTypes.object,
  validation: PropTypes.object,
};

export default InputComponent;
